import React from 'react';
import moment from 'moment';

import { IBoxProps } from '@styleseat/ui';
import fbAPI from '../../../../../modules/fbAPI';

import { IReview } from '../../../../../modules/provider/reviews';

import Review from './Review';
import { ClientProviderProfile } from '../../../../../api/ProfileClientView';
import { IBasicImage } from '../../../../../api/Providers/GalleryImages';

interface IReviewContainerProps extends IReview {
  image?: any,
  isProvider: boolean,
  provider: ClientProviderProfile,
  reviewImages?: Array<IBasicImage>,
  reviewText?: string,
  /** True to suppress padding of the main container */
  noPadding?: boolean;
  /** Average provider rating */
  ratingCount?: number;
  /** Number of provider reviews */
  reviewCount?: number;
  /** Text to display in an action button */
  actionText?: string;
  numStars?: number;
  creationTime?: string;
  /** True to hide the respond button, otherwise false */
  hideRespond?: boolean;
  /** True to hide separator at bottom of component */
  hideSeparator?: boolean;
  /** True to hide review tags */
  hideTags?: boolean,
  /** True to hide review images */
  hideImages?: boolean,
  /** True to display text indicating that this is a new client for the pro */
  isNewClient?: boolean;
  /** True if we are the first review in a list */
  isFirst?: boolean;
  /** True if we are the last review in a list */
  isLast?: boolean;
  getFacebookReviewShareUrl?: (review: IReview) => string,
  onPinReview?: (review: IReview) => void,
  onUnpinReview?: (review: IReview) => void,
  highlighted?: boolean,
  onReviewImageSelected?: (args: { id: number | string, imageCount: number }) => void,
  onReviewImageShown?: (
    args: { id: number | string, imageCount: number, imageIndex: number }
  ) => void,
  openResponseModal?: (review: IReview, edit: boolean) => void,
  trackShare?: (review: IReview) => void,
  /** Called when the button with the actionText is clicked */
  onAction?: (review: IReview) => void,
  /** True to condense the rating stars, otherwise false. */
  condenseStars?: boolean,
  /** True to hide client information, otherwise false. */
  hideClient?: boolean,
  isInReviewTagFiltersTest?: boolean;
}

const ReviewContainer = (props: IReviewContainerProps & Omit<IBoxProps, 'style'>) => {
  const {
    review_text: text,
    reviewText = text,
    appointment,
    response,
    provider,
    creation_time: time,
    creationTime = time,
    num_stars: stars,
    numStars = stars,
    isProvider,
    pinned,
    highlighted,
    image,
    reviewImages = [],
    about_hairtype: aboutHairType = [],
    about_location: aboutLocation = [],
    about_provider: aboutProvider = [],
    noPadding,
    ratingCount,
    reviewCount,
    actionText,
    hideRespond,
    hideSeparator,
    hideTags,
    hideImages,
    isNewClient,
    isFirst = false,
    isLast = false,
    condenseStars,
    hideClient,
    openResponseModal,
    onPinReview,
    onUnpinReview,
    getFacebookReviewShareUrl,
    trackShare,
    onReviewImageSelected,
    onReviewImageShown,
    onAction,
    isInReviewTagFiltersTest,
    ...boxProps
  } = props;

  const {
    first_name: firstName,
    last_name: lastName,
  } = appointment?.client?.user || {};
  const { service_name: serviceName } = appointment || {};
  const { text: responseText } = response || {};
  const reviewDate = React.useMemo(() => moment.utc(creationTime)
    .local()
    .format('MMM D, YYYY'), [creationTime]);
  const canPinAndShare: boolean = isProvider && !!reviewText?.length && !!appointment;
  const shouldShowRespondButton = canPinAndShare && !responseText && appointment && !hideRespond;
  const canShare = typeof getFacebookReviewShareUrl === 'function';

  const [openGalleryImageAtIndex, setOpenGalleryImageAtIndex] = React.useState<number | null>(null);
  const resetOpenGalleryImageAtIndex = () => setOpenGalleryImageAtIndex(null);

  const onEdit = () => {
    if (typeof openResponseModal === 'function') {
      openResponseModal(props, !!responseText && !!responseText.length && isProvider);
    }
  };

  const handlePinIconPress = () => {
    if (canPinAndShare) {
      if (!pinned && typeof onPinReview === 'function') {
        onPinReview(props);
      } else if (pinned && typeof onUnpinReview === 'function') {
        onUnpinReview(props);
      }
    }
  };

  const handleFacebookShare = () => {
    if (canShare) {
      fbAPI.share({
        url: getFacebookReviewShareUrl(props),
        title: `${firstName} ${lastName} recommended me`,
        text: reviewText,
        image,
      });
    }
    trackShare?.(props);
  };

  /** Handles pressing of the action button */
  const handleActionPress = () => {
    onAction?.(props);
  };

  const handleImagePress = (imageIndex: number) => {
    setOpenGalleryImageAtIndex(imageIndex);
    const reviewImage = reviewImages?.[imageIndex];
    if (typeof onReviewImageSelected === 'function') {
      onReviewImageSelected({
        ...reviewImage,
        imageCount: reviewImages.length,
      });
    }
    if (typeof onReviewImageShown === 'function') {
      onReviewImageShown({
        ...reviewImage,
        imageIndex,
        imageCount: reviewImages.length,
      });
    }
  };

  const handleImageShown = (index: number) => {
    const reviewImage = reviewImages?.[index];
    if (typeof onReviewImageShown === 'function') {
      onReviewImageShown({
        ...reviewImage,
        imageIndex: index,
        imageCount: reviewImages.length,
      });
    }
  };

  return (
    <Review
      highlighted={highlighted}
      isInReviewTagFiltersTest={isInReviewTagFiltersTest}
      noPadding={noPadding}
      isFirst={isFirst}
      isLast={isLast}
      numStars={numStars}
      ratingCount={ratingCount}
      reviewCount={reviewCount}
      condenseStars={condenseStars}
      serviceName={serviceName}
      canPinAndShare={!!canPinAndShare}
      onPinIconPress={handlePinIconPress}
      pinned={pinned}
      canShare={canShare}
      onFacebookShare={handleFacebookShare}
      reviewText={reviewText}
      hideTags={hideTags}
      hideClient={hideClient}
      aboutHairType={aboutHairType}
      aboutProvider={aboutProvider}
      aboutLocation={aboutLocation}
      responseText={responseText}
      isProvider={isProvider}
      firstName={firstName}
      lastName={lastName}
      reviewDate={reviewDate}
      isNewClient={isNewClient}
      actionText={actionText}
      onActionPress={handleActionPress}
      hideSeparator={hideSeparator}
      provider={provider}
      shouldShowRespondButton={shouldShowRespondButton}
      openGalleryImageAtIndex={openGalleryImageAtIndex}
      resetOpenGalleryImageAtIndex={resetOpenGalleryImageAtIndex}
      hideImages={hideImages}
      reviewImages={reviewImages}
      onImagePress={handleImagePress}
      onEdit={onEdit}
      onImageShown={handleImageShown}
      boxProps={boxProps}
    />
  );
};

export default ReviewContainer;
